import { useMemo, useState } from "react";
import { router } from "@inertiajs/react";
import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import Panel from "@/components/ui/Panel";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import EmptyState from "@/components/ui/EmptyState";
import { Select, Checkbox } from "@/components/ui/Field";

type Flag = string;

// One event's signup mix, and whether its RSVP grant is frozen. Mirrors
// EventFundingRisk#display_hash plus the columns the row needs to act.
type EventRisk = {
  id: number;
  name: string;
  verified: number;
  flagged: number;
  ratio: number;
  // What an automatic hold would say about this event today.
  would_hold: boolean;
  held: boolean;
  hold_reason: string | null;
  at_stake_cents: number;
};

type SignupRow = {
  id: number;
  event_id: number;
  event_name: string | null;
  name: string | null;
  email: string | null;
  heard_about: string | null;
  aasm_state: string;
  confirmed: boolean;
  verified: boolean;
  created_at: string;
  signup_ip: string | null;
  device: string | null;
  disposable_email: boolean;
  ip_signup_count: number;
  flags: Flag[];
  funding_excluded: boolean;
  funding_exclusion_reason?: string | null;
};

type Cluster = {
  ip: string;
  signups: number;
  events: number;
  event_names: string[];
  verified: number;
  first_at: string;
  last_at: string;
  devices: string[];
};

type Summary = {
  total: number;
  verified: number;
  flagged: number;
  flagged_verified: number;
  at_risk_cents: number;
};

function money(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function when(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "warning";
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-caption text-ink-soft">{label}</span>
      <span
        className={
          tone === "warning"
            ? "font-display text-heading text-sunset-orange font-bold"
            : "font-display text-heading text-ink font-bold"
        }
      >
        {value}
      </span>
      {hint && <span className="text-caption text-ink-soft">{hint}</span>}
    </div>
  );
}

function SignupRowView({
  row,
  flagLabels,
  selected,
  onToggle,
}: {
  row: SignupRow;
  flagLabels: Record<string, string>;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-line/20 flex flex-wrap items-start gap-3 border-b py-2.5 last:border-b-0">
      <Checkbox
        checked={selected}
        onChange={onToggle}
        className="mt-1"
        aria-label={`Select ${row.name ?? row.email ?? "signup"}`}
      />
      <div className="min-w-0 flex-1 basis-56">
        <p className="text-body text-ink truncate font-bold">
          {row.name ?? "Someone"}
        </p>
        <p className="text-caption text-ink-soft font-literal truncate">
          {row.email}
        </p>
      </div>

      <div className="min-w-0 basis-40">
        <p className="text-caption text-ink-muted truncate">
          {row.event_name ?? "-"}
        </p>
        <p className="text-caption text-ink-soft">{when(row.created_at)}</p>
      </div>

      <div className="min-w-0 basis-44">
        <p className="text-caption text-ink-muted font-literal truncate">
          {row.signup_ip ?? "no address recorded"}
          {row.ip_signup_count > 1 && (
            <span className="text-ink-soft"> ×{row.ip_signup_count}</span>
          )}
        </p>
        <p className="text-caption text-ink-soft truncate">
          {row.device ?? "-"}
        </p>
      </div>

      <div className="min-w-0 basis-36">
        <p className="text-caption text-ink-soft truncate">
          {row.heard_about ?? "-"}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {row.funding_excluded && <Badge tone="neutral">Not funded</Badge>}
        {row.verified ? (
          <Badge tone="success">Verified</Badge>
        ) : row.confirmed ? (
          <Badge tone="neutral">Confirmed</Badge>
        ) : (
          <Badge tone="warning">Unconfirmed</Badge>
        )}
        {row.flags.map((flag) => (
          <Badge key={flag} tone="warning">
            {flagLabels[flag] ?? flag}
          </Badge>
        ))}
      </div>
    </div>
  );
}

export default function AdminSignups({
  rows,
  summary,
  clusters,
  flag_labels,
  events,
  filters,
  truncated,
  total_count,
  excluded_count,
  event_risk,
}: {
  rows: SignupRow[];
  summary: Summary;
  clusters: Cluster[];
  flag_labels: Record<string, string>;
  events: { id: number; name: string }[];
  event_risk: EventRisk[];
  filters: { event_id: number | null; flagged: boolean };
  truncated: boolean;
  total_count: number;
  excluded_count: number;
}) {
  const [flaggedOnly, setFlaggedOnly] = useState(filters.flagged);
  const [selected, setSelected] = useState<number[]>([]);

  function toggle(id: number) {
    setSelected((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
    );
  }

  // Acting on a set is the point: these arrive in clusters of thirty.
  function act(path: string, confirmText: string, withReason: boolean) {
    if (selected.length === 0) return;
    let reason: string | null = null;
    if (withReason) {
      reason = window.prompt(
        `${confirmText} (${selected.length}). Why? The organizer sees this.`,
        "Signed up in bulk from one connection",
      );
      if (reason === null) return;
    } else if (!window.confirm(`${confirmText} ${selected.length}?`)) {
      return;
    }
    router.post(
      path,
      { ids: selected, reason },
      { preserveScroll: true, onSuccess: () => setSelected([]) },
    );
  }

  const visible = useMemo(
    () => (flaggedOnly ? rows.filter((row) => row.flags.length > 0) : rows),
    [rows, flaggedOnly],
  );

  function filterByEvent(value: string) {
    router.get("/admin/signups", value ? { event_id: value } : {}, {
      preserveState: true,
      preserveScroll: true,
    });
  }

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          title="Signups"
          subtitle="Where RSVPs came from. Every flag here is a signal, not a verdict - a shared address is what a friend group on one hotspot looks like."
        />

        <Panel title="summary.txt">
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="Signups" value={String(summary.total)} />
            <Stat
              label="Verified"
              value={String(summary.verified)}
              hint="confirmed + in the age window"
            />
            <Stat label="Flagged" value={String(summary.flagged)} />
            <Stat
              label="Flagged & verified"
              value={String(summary.flagged_verified)}
              hint="the ones that would be paid"
              tone={summary.flagged_verified > 0 ? "warning" : undefined}
            />
            <Stat
              label="Not funded"
              value={String(excluded_count)}
              hint="already taken off grants"
            />
            <Stat
              label="At risk"
              value={money(summary.at_risk_cents)}
              hint="grant resting on flagged signups"
              tone={summary.at_risk_cents > 0 ? "warning" : undefined}
            />
          </div>
        </Panel>

        {event_risk.length > 0 && (
          <Panel title="grant-holds.csv">
            <p className="text-caption text-ink-muted mb-3">
              Events by the share of their <em>paid</em> signups that carry a
              flag. Holding freezes the RSVP half of the grant - the event runs
              as normal, nobody leaves the roster, and coding hours are
              untouched.
            </p>
            <div className="flex flex-col">
              {event_risk.map((row) => (
                <div
                  key={row.id}
                  className="border-line/20 flex flex-wrap items-center gap-3 border-b py-2.5 last:border-b-0"
                >
                  <p className="text-body text-ink min-w-0 flex-1 basis-44 truncate font-bold">
                    {row.name}
                  </p>
                  <span className="text-caption text-ink-muted basis-40">
                    {row.flagged}/{row.verified} flagged (
                    {Math.round(row.ratio * 100)}%)
                  </span>
                  <span className="text-caption text-ink-soft basis-28">
                    {money(row.at_stake_cents)}
                  </span>
                  {row.held ? (
                    <>
                      <Badge tone="warning">On hold</Badge>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          router.post(
                            `/admin/events/${row.id}/release_funding`,
                            {},
                            { preserveScroll: true },
                          )
                        }
                      >
                        Release
                      </Button>
                    </>
                  ) : (
                    <>
                      {row.would_hold && (
                        <Badge tone="warning">Over half</Badge>
                      )}
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          const reason = window.prompt(
                            `Why is ${row.name}'s signup grant going on hold? The organizer sees this.`,
                            "Held for signup review.",
                          );
                          if (reason === null) return;
                          router.post(
                            `/admin/events/${row.id}/hold_funding`,
                            { reason },
                            { preserveScroll: true },
                          );
                        }}
                      >
                        Hold
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </Panel>
        )}

        {clusters.length > 0 && (
          <Panel title="clusters.csv">
            <p className="text-caption text-ink-muted mb-3">
              Addresses with several signups behind them, worst first. Start
              here - the table below is for confirming what this points at.
            </p>
            <div className="flex flex-col">
              {clusters.map((cluster) => (
                <div
                  key={cluster.ip}
                  className="border-line/20 flex flex-wrap items-center gap-3 border-b py-2.5 last:border-b-0"
                >
                  <p className="text-body text-ink font-literal min-w-0 flex-1 basis-44 truncate font-bold">
                    {cluster.ip}
                  </p>
                  <span className="text-caption text-ink-muted basis-32">
                    {cluster.signups} signups
                    {cluster.verified > 0 && ` · ${cluster.verified} verified`}
                  </span>
                  <span className="text-caption text-ink-soft min-w-0 flex-1 basis-48 truncate">
                    {cluster.event_names.join(", ")}
                  </span>
                  <span className="text-caption text-ink-soft basis-40">
                    {when(cluster.first_at)} → {when(cluster.last_at)}
                  </span>
                  {cluster.events > 1 && (
                    <Badge tone="warning">{cluster.events} events</Badge>
                  )}
                </div>
              ))}
            </div>
          </Panel>
        )}

        <Panel title={`signups.csv - ${visible.length}`}>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <Select
              value={filters.event_id ?? ""}
              onChange={(e) => filterByEvent(e.target.value)}
              aria-label="Filter by event"
              className="max-w-xs"
            >
              <option value="">Every event</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name}
                </option>
              ))}
            </Select>

            <Button
              size="sm"
              variant={flaggedOnly ? "primary" : "secondary"}
              onClick={() => setFlaggedOnly((on) => !on)}
            >
              {flaggedOnly ? "Showing flagged only" : "Show flagged only"}
            </Button>

            {truncated && (
              <span className="text-caption text-ink-soft">
                Newest {rows.length} of {total_count} - flags are computed over
                these, so filter by event for a full picture of one.
              </span>
            )}
          </div>

          <div className="border-line/30 mb-3 flex flex-wrap items-center gap-2 border-b pb-3">
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                setSelected(
                  selected.length === visible.length
                    ? []
                    : visible.map((r) => r.id),
                )
              }
            >
              {selected.length === visible.length && visible.length > 0
                ? "Clear selection"
                : `Select all ${visible.length}`}
            </Button>

            <span className="text-caption text-ink-muted">
              {selected.length} selected
            </span>

            <div className="flex flex-wrap gap-2 sm:ml-auto">
              <Button
                size="sm"
                disabled={selected.length === 0}
                onClick={() =>
                  act(
                    "/admin/signups/exclude",
                    "Stop funding these signups",
                    true,
                  )
                }
              >
                Don't fund these
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={selected.length === 0}
                onClick={() =>
                  act("/admin/signups/restore", "Fund these again", false)
                }
              >
                Fund again
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={selected.length === 0}
                onClick={() =>
                  act(
                    "/admin/signups/remove",
                    "Remove these from the roster:",
                    false,
                  )
                }
              >
                Remove
              </Button>
            </div>
          </div>

          {visible.length === 0 ? (
            <EmptyState
              title={flaggedOnly ? "Nothing flagged" : "No signups yet"}
              description={
                flaggedOnly
                  ? "No signup in this set tripped any of the signals."
                  : "RSVPs will appear here as they come in."
              }
            />
          ) : (
            <div className="flex flex-col">
              {visible.map((row) => (
                <SignupRowView
                  key={row.id}
                  row={row}
                  flagLabels={flag_labels}
                  selected={selected.includes(row.id)}
                  onToggle={() => toggle(row.id)}
                />
              ))}
            </div>
          )}
        </Panel>
      </div>
    </Layout>
  );
}
